// Enum-value completion emission (autocomplete Phase 3).
//
// When the cursor sits at a comparison value operand against an
// enum-classified column (`WHERE status = '<cursor>'`,
// `status IN ('<cursor>'`, `status <> '<cursor>'`), the resolver pulls the
// column's cached distinct values from the shared MetaInfo hint provider
// (Phase 1's `getEnumValues`) and emits them as string-literal completion
// candidates ranked high (tier 6 — same as JSON-path hints, so they sort
// above generic keywords).
//
// Mirrors `emitJsonPathCandidates.ts`: a pure emitter that pushes
// `CompletionCandidate`s into `out` and returns the count of *value*
// candidates emitted (so callers can short-circuit the generic
// string-literal path). When the cached value set was truncated at the
// MetaInfo 200-distinct cap, a trailing non-selectable note item is appended
// (it is NOT counted in the return value — it carries no insertable text).
//
// Path-aware extension (MERGE-2): when `path` is provided (a canonical
// JSONPath e.g. `$.status`), the lookup is path-keyed. If the exact path
// misses, the wildcard probe replaces each `[N]` segment with `[*]` and
// tries the wildcard-sampled cache — values from a wildcard sample like
// `$.items[*].status` are offered for `$.items[3].status`.

import type { CompletionCandidate, CompletionCandidateTier } from '@/core/engines';
import { getEnumValues } from '@/features/sql-resolver/metaInfoHints/metaInfoHintProvider';

export type EmitEnumValueCandidatesArgs = {
  readonly connectionId: string;
  readonly database: string;
  readonly schema: string;
  readonly table: string;
  readonly column: string;
  /**
   * Optional canonical JSONPath (e.g. `$.status`, `$.items[3].status`).
   * When provided, performs a path-keyed enum lookup. Falls back to wildcard
   * probe when the exact path misses. Omit (or pass empty string) for the
   * column-level enum lookup (original behavior).
   */
  readonly path?: string;
  /**
   * Maximum *value* candidates to emit. Defaults to 64 (PRD JCS-9 sibling
   * cap, matching `emitJsonPathCandidates`). The MetaInfo enum cache itself
   * is already bounded at 200 distinct values; this is the per-completion
   * list budget.
   */
  readonly maxCandidates?: number;
  /**
   * Whether to wrap each value in single quotes. Default `true` — the
   * standalone-literal case (`status = |`). Pass `false` when the cursor is
   * already inside an in-progress `'…` literal so the surrounding quotes are
   * not duplicated (mirrors `emitJsonPathCandidates`' bare insert). Embedded
   * quotes are SQL-escaped either way.
   */
  readonly quote?: boolean;
  /**
   * Candidate tier for the emitted values (and the truncated-note row).
   * Default 6 — the historical operator-position tier. The value-slot
   * resolved-target path (T18) passes 1 (D7).
   */
  readonly tier?: CompletionCandidateTier;
  /**
   * Complete, ordered member list to emit when the MetaInfo cache lookup
   * misses (cold / undeclared native enum). MySQL native `ENUM` / `SET`
   * members travel inline in the column type, so the resolver can surface
   * them synchronously without the MetaInfo IPC the declared-enum path uses.
   * Because the list is complete by construction, no truncated-note item is
   * appended. The MetaInfo hint always wins when present.
   */
  readonly nativeFallbackMembers?: readonly string[];
};

/**
 * Emit enum-value candidates into the completion list. Returns the number of
 * *value* candidates emitted (excludes the truncated-note item). 0 on a cold
 * / stale / missing-column miss.
 */
export function emitEnumValueCandidates(
  out: CompletionCandidate[],
  args: EmitEnumValueCandidatesArgs,
): number {
  const cap = Math.max(0, args.maxCandidates ?? 64);
  const quote = args.quote ?? true;
  // Value-slot plan T7 (D7/G26): resolved-target emission (T18) surfaces enum
  // values at tier 1; every existing caller keeps the historical tier 6.
  const tier = args.tier ?? 6;
  const hint = resolveEnumHint(args);
  if (hint && hint.values.length > 0) {
    const slice = hint.values.slice(0, cap);
    for (const v of slice) {
      out.push(valueToCandidate(v, quote, tier));
    }
    if (hint.truncated) {
      out.push(truncatedNoteCandidate(tier));
    }
    return slice.length;
  }
  // MetaInfo miss → fall back to the engine's inline native members (MySQL
  // `ENUM` / `SET`). The list is complete, so no truncated-note item.
  const native = args.nativeFallbackMembers;
  if (native && native.length > 0) {
    const slice = native.slice(0, cap);
    for (const v of slice) {
      out.push(valueToCandidate(v, quote, tier));
    }
    return slice.length;
  }
  return 0;
}

// ─────────────────────────────────────────────────────────────────────────────
// Hint resolution — exact path, wildcard probe, then column-level fallback
// ─────────────────────────────────────────────────────────────────────────────

type EnumHint = { values: readonly string[]; truncated: boolean };

/**
 * Resolve the best enum hint for the given args:
 *   1. If `path` is non-empty, try exact path lookup.
 *   2. On miss, derive the wildcard path (`[N]` → `[*]`) and probe again.
 *      On a concrete-path miss, derives the wildcard form (`[N]` → `[*]`) and
 *      tries one lookup. Returns `undefined` if neither the exact path nor its
 *      wildcard form is cached. `truncated` flag propagates from whichever
 *      entry returned a hit.
 *   3. If `path` is empty/omitted, column-level lookup (original behavior).
 */
function resolveEnumHint(args: EmitEnumValueCandidatesArgs): EnumHint | undefined {
  const path = args.path ?? '';

  // Column-level lookup (original behavior).
  if (path === '') {
    return getEnumValues(args.connectionId, args.database, args.schema, args.table, args.column, '');
  }

  // Exact path lookup.
  const exact = getEnumValues(
    args.connectionId, args.database, args.schema, args.table, args.column, path,
  );
  if (exact !== undefined) return exact;

  // Wildcard probe: replace every `[N]` (concrete array index) with `[*]`.
  const wildcardPath = path.replace(/\[\d+\]/g, '[*]');
  if (wildcardPath === path) return undefined; // no concrete indices — no probe needed

  const wildcard = getEnumValues(
    args.connectionId, args.database, args.schema, args.table, args.column, wildcardPath,
  );
  return wildcard;
}

function valueToCandidate(
  value: string,
  quote: boolean,
  tier: CompletionCandidateTier,
): CompletionCandidate {
  // `insertText` is the value as a SQL string literal — single-quoted when
  // standalone, BARE when the cursor is already inside a `'…` literal — with
  // embedded `'` doubled per SQL escaping either way. `filterText` is the raw
  // value so prefix matching works as the user types `op` → `open`. Tier 6
  // (default) + kind `snippet` mirrors `emitJsonPathCandidates`' candidate
  // shape so values outrank generic keyword candidates.
  const escaped = value.replace(/'/g, "''");
  return {
    label: value,
    insertText: quote ? `'${escaped}'` : escaped,
    isSnippet: false,
    kind: 'snippet',
    tier,
    detail: 'enum value',
    filterText: value,
  };
}

function truncatedNoteCandidate(tier: CompletionCandidateTier): CompletionCandidate {
  // Non-selectable informational row. Empty `insertText` so picking it does
  // nothing; `filterText` is a string the user is extremely unlikely to type
  // so it sinks to the bottom of fuzzy results. The shared tier keeps it
  // grouped with its value siblings.
  return {
    label: 'values truncated at 200 — not all distinct values shown',
    insertText: '',
    isSnippet: false,
    kind: 'snippet',
    tier,
    detail: 'enum cache truncated',
    filterText: '​__enum_truncated_note__',
  };
}
